import assertNever from "assert-never";
import classNames from "classnames";
import React from "react";
import * as Ons from "react-onsenui";
import {
  CHECK_OUTLINED_ICON,
  INFO_OUTLINED_ICON,
  TIMES_OUTLINED_ICON,
} from "../../../utils/svgs_new";
import { AppButton, AppButtonProps } from "../AppButton";
import { AppIcon } from "../AppIcon";
import { AppAbstractModal, AppAbstractModalProps } from "./AppAbstractModal";
import ModalActions from "./components/ModalActions/ModalActions";
import ModalHeadline from "./components/ModalHeadline/ModalHeadline";

export type AppModalAction = AppButtonProps & { key: string };

export interface AppModalProps extends AppAbstractModalProps {
  type: "info" | "success" | "error" | "neutral";
  title: string | null;
  message?: string;
  actions: AppModalAction[];
  isLoading?: boolean;
  icon?: React.ReactNode;
  options?: {
    messageAsHtml?: boolean;
  };
  children?: React.ReactNode;
}

const AppModal = React.forwardRef((props: AppModalProps, ref: React.Ref<HTMLDivElement>) => {
  const icon = React.useMemo(() => {
    if (props.icon !== undefined) {
      return props.icon;
    }

    switch (props.type) {
      case "error":
        return <AppIcon icon={TIMES_OUTLINED_ICON} size={72} className="modal-icon" />;
      case "info":
        return <AppIcon icon={INFO_OUTLINED_ICON} size={72} className="modal-icon" />;
      case "success":
        return <AppIcon icon={CHECK_OUTLINED_ICON} size={72} className="modal-icon" />;
      case "neutral":
        return null;
      default:
        assertNever(props.type);
    }
  }, [props.icon, props.type]);

  return (
    <AppAbstractModal
      isOpen={props.isOpen}
      className={classNames("AppModal", props.className)}
      data-type={props.type}
      onRequestClose={props.onRequestClose}
      withAnimatePresence={props.withAnimatePresence}
      ref={ref}
    >
      {icon}

      {props.title !== null ? <ModalHeadline>{props.title}</ModalHeadline> : null}

      {props.isLoading === true ? (
        <div style={{ margin: 16 }}>
          <Ons.ProgressCircular indeterminate={true} />
        </div>
      ) : (
        <>
          <p className="modal-message">
            {props.options?.messageAsHtml === true && props.message !== undefined ? (
              <span dangerouslySetInnerHTML={{ __html: props.message }} />
            ) : (
              <>{props.message}</>
            )}
          </p>
          {props.children}
          <ModalActions>
            {props.actions.map((btnProps) => (
              <AppButton {...btnProps} key={btnProps.key} ref={null} />
            ))}
          </ModalActions>
        </>
      )}
    </AppAbstractModal>
  );
});

export const InfoModal = React.forwardRef(
  (props: Omit<AppModalProps, "type">, ref: React.Ref<HTMLDivElement>) => (
    <AppModal type="info" {...props} ref={ref} />
  )
);

export const SuccessModal = React.forwardRef(
  (props: Omit<AppModalProps, "type">, ref: React.Ref<HTMLDivElement>) => (
    <AppModal type="success" {...props} ref={ref} />
  )
);

export const ErrorModal = React.forwardRef(
  (props: Omit<AppModalProps, "type">, ref: React.Ref<HTMLDivElement>) => (
    <AppModal type="error" {...props} ref={ref} />
  )
);

export const LoadingModal = React.forwardRef(
  (
    props: Omit<AppModalProps, "title" | "type" | "actions" | "isLoading">,
    ref: React.Ref<HTMLDivElement>
  ) => <AppModal type="neutral" actions={[]} isLoading={true} title={null} {...props} ref={ref} />
);

export default AppModal;
